/**
 * Accept Invite Page
 * Handles invite token redemption flow
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';


export const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const { refreshTenant } = useTenant();
  
  const [inviteToken, setInviteToken] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Extract invite token from URL
    const token = searchParams.get('token') || searchParams.get('invite');
    if (token) {
      setInviteToken(token);
      // Store in session storage for use after login
      sessionStorage.setItem('pendingInviteToken', token);
    }
  }, [searchParams]);

  useEffect(() => {
    // If user is authenticated and we have a token, process it automatically
    if (isAuthenticated && inviteToken && !isProcessing && !success) {
      handleAcceptInvite();
    }
  }, [isAuthenticated, inviteToken]);

  const handleAcceptInvite = async () => {
    if (!inviteToken) {
      setError('No invitation token provided');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Store token in session storage
      sessionStorage.setItem('pendingInviteToken', inviteToken);

      // Refresh tenant context - this will pass the invite token to the backend
      await refreshTenant();

      // Clear the token from session storage
      sessionStorage.removeItem('pendingInviteToken');

      setSuccess(true);

      // Redirect to home after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      setError(err.message || 'Failed to accept invitation');
      sessionStorage.removeItem('pendingInviteToken');
    } finally {
      setIsProcessing(false);
    }
  };

  const wrap = 'min-h-screen bg-surface-app flex items-center justify-center p-6';
  const card = 'w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden';
  const hdr  = 'px-8 pt-7 pb-5 border-b border-gray-100';
  const bdy  = 'px-8 py-6 flex flex-col gap-4';

  if (!isAuthenticated) {
    return (
      <div className={wrap}><div className={card}>
        <div className={hdr}>
          <h2 className="text-2xl font-semibold text-gray-900 mb-1">Accept Invitation</h2>
          <p className="text-body text-gray-500">You need to sign in to accept this invitation.</p>
        </div>
        <div className={bdy}>
          <div className="bg-info-light border border-info/30 rounded-lg p-4 text-label text-info-dark">
            Your invitation token has been saved. Please sign in to continue.
          </div>
          <button type="button" onClick={() => window.location.reload()}
            className="w-full py-3.5 bg-blue-500 hover:bg-blue-600 text-white text-body font-semibold rounded-md transition-colors duration-fast">
            Sign In
          </button>
        </div>
      </div></div>
    );
  }

  if (success) {
    return (
      <div className={wrap}><div className={card}>
        <div className={hdr}>
          <h2 className="text-2xl font-semibold text-gray-900 mb-1">✅ Invitation Accepted!</h2>
          <p className="text-body text-gray-500">Welcome to your new workspace.</p>
        </div>
        <div className={bdy}>
          <div className="bg-success-light border border-success/30 rounded-lg p-4 text-label text-success-dark">
            You have successfully joined the workspace. Redirecting…
          </div>
        </div>
      </div></div>
    );
  }

  return (
    <div className={wrap}><div className={card}>
      <div className={hdr}>
        <h2 className="text-2xl font-semibold text-gray-900 mb-1">Accept Invitation</h2>
        <p className="text-body text-gray-500">Processing your invitation…</p>
      </div>
      <div className={bdy}>
        {error && (
          <div className="bg-error-light border border-error/30 rounded-lg p-4 text-label text-error">
            <strong>Error:</strong> {error}
          </div>
        )}
        {isProcessing && (
          <div className="bg-info-light border border-info/30 rounded-lg p-4 text-label text-info-dark">
            Please wait while we process your invitation…
          </div>
        )}
      </div>
    </div></div>
  );
};

